import { zodResolver } from '@hookform/resolvers/zod';
import { CheckIcon, ImageIcon, Trash2Icon, UploadIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { JobMark } from '@/components/JobMark';
import { SectionTitle } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Field, FormError } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useJobContext } from '@/layouts/JobLayout';
import { errorMessage } from '@/lib/api';
import { useMe } from '@/lib/auth';
import { HEX_COLOR_RE, JOB_ACCENT_PRESETS, JOB_COVER_MAX_MB, JOB_COVER_TYPES, JOB_ICONS, jobAccent } from '@/lib/jobStyle';
import { cn } from '@/lib/utils';
import { LocationSection } from './LocationSection';
import { useAvailableNumbers, useJobCover, useUpdateJob } from './queries';

const schema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(120),
  description: z.string().trim().max(2000),
  senderLabel: z.string().trim().max(40),
});
type Values = z.infer<typeof schema>;

const NONE = '__none__';

export function JobSettingsPage() {
  const { job, permissions } = useJobContext();
  const update = useUpdateJob(job.id);
  const [error, setError] = useState<string | null>(null);
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { name: job.name, description: job.description ?? '', senderLabel: job.senderLabel ?? '' },
  });
  const label = form.watch('senderLabel');

  const onSubmit = form.handleSubmit(async (v) => {
    setError(null);
    try {
      await update.mutateAsync({ name: v.name, description: v.description || null, senderLabel: v.senderLabel || null });
      toast.success('Job updated');
      form.reset(v);
    } catch (err) {
      setError(errorMessage(err));
    }
  });

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-start lg:gap-8">
      <div className="grid content-start gap-6 lg:gap-8">
        <section className="bg-card border p-6">
          <SectionTitle className="mb-5">Job details</SectionTitle>
          <form onSubmit={onSubmit} className="grid gap-5" noValidate>
            <FormError message={error} />
            <Field label="Name" htmlFor="name" required error={form.formState.errors.name?.message}>
              <Input id="name" disabled={!permissions.editJob} {...form.register('name')} />
            </Field>
            <Field label="Description" htmlFor="description" error={form.formState.errors.description?.message}>
              <Textarea id="description" rows={3} disabled={!permissions.editJob} {...form.register('description')} />
            </Field>
            <Field
              label="Sender label"
              htmlFor="senderLabel"
              error={form.formState.errors.senderLabel?.message}
              description={
                <>
                  Prefixed to every message so recipients know who is texting. Example: <span className="text-foreground font-medium">{label ? `${label}: ` : ''}Gate B is closed tomorrow. Use Gate A.</span>
                </>
              }
            >
              <Input id="senderLabel" maxLength={40} placeholder="e.g. Orlando Tower" disabled={!permissions.editJob} {...form.register('senderLabel')} />
            </Field>
            {permissions.editJob && (
              <div className="flex justify-end">
                <Button type="submit" disabled={update.isPending || !form.formState.isDirty}>
                  {update.isPending ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            )}
          </form>
        </section>
        <AppearanceSection />
        <LocationSection />
      </div>

      <div className="grid content-start gap-6 lg:gap-8">
        <PhoneNumberSection />
        <QuietHoursSection />
        {permissions.archiveJob && <ArchiveSection />}
      </div>
    </div>
  );
}

/**
 * The job's look: a cover photo, an accent colour and an icon. They show on the job list, the job
 * header, the sidebar and the command palette, and the operations console can set them too.
 */
function AppearanceSection() {
  const { job, permissions } = useJobContext();
  const update = useUpdateJob(job.id);
  const cover = useJobCover(job.id);
  const fileRef = useRef<HTMLInputElement>(null);
  const canEdit = permissions.editJob;
  const [accent, setAccent] = useState<string>(job.accentColor ?? '');
  const [icon, setIcon] = useState<string>(job.icon ?? '');
  const [custom, setCustom] = useState<string>(job.accentColor && !JOB_ACCENT_PRESETS.some((p) => p.hex === job.accentColor) ? job.accentColor : '');
  const customValid = custom === '' || HEX_COLOR_RE.test(custom);
  const dirty = accent !== (job.accentColor ?? '') || icon !== (job.icon ?? '');
  const preview = { name: job.name, accentColor: accent || null, icon: icon || null, coverUrl: job.coverUrl };

  const save = async () => {
    try {
      await update.mutateAsync({ accentColor: accent || null, icon: icon || null });
      toast.success('Appearance saved');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const pick = async (file: File | undefined) => {
    if (!file) return;
    if (!JOB_COVER_TYPES.includes(file.type)) {
      toast.error('Use a PNG, JPEG, GIF or WebP image');
      return;
    }
    if (file.size > JOB_COVER_MAX_MB * 1024 * 1024) {
      toast.error(`That image is ${Math.round(file.size / 1024 / 1024)} MB. Keep covers under ${JOB_COVER_MAX_MB} MB.`);
      return;
    }
    try {
      await cover.upload.mutateAsync(file);
      toast.success('Cover photo updated');
    } catch (err) {
      toast.error(errorMessage(err, 'Upload failed'));
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = async () => {
    try {
      await cover.remove.mutateAsync();
      toast.success('Cover photo removed');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <section id="appearance" className="bg-card border p-6">
      <SectionTitle className="mb-2">Appearance</SectionTitle>
      <p className="text-muted-foreground mb-5 text-sm">A photo of the site, a colour and an icon make the job easy to spot in the job list, the sidebar and search.</p>
      <div className="grid gap-6">
        {/* Cover photo */}
        <div className="grid gap-2">
          <Label>Cover photo</Label>
          <div className="flex items-start gap-4">
            <div className="bg-muted relative h-24 w-40 shrink-0 overflow-hidden border" style={{ borderColor: jobAccent(preview) }}>
              {job.coverUrl ? (
                <img src={job.coverUrl} alt="" className="size-full object-cover" />
              ) : (
                <div className="text-muted-foreground flex size-full flex-col items-center justify-center gap-1 text-xs">
                  <ImageIcon className="size-5" aria-hidden />
                  No photo yet
                </div>
              )}
            </div>
            <div className="grid gap-2">
              <p className="text-muted-foreground text-xs leading-relaxed">PNG, JPEG, GIF or WebP up to {JOB_COVER_MAX_MB} MB. Landscape works best; it is cropped to fit.</p>
              {canEdit && (
                <div className="flex flex-wrap gap-2">
                  <input ref={fileRef} type="file" accept={JOB_COVER_TYPES.join(',')} className="sr-only" onChange={(e) => void pick(e.target.files?.[0])} />
                  <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={cover.upload.isPending}>
                    <UploadIcon /> {cover.upload.isPending ? 'Uploading…' : job.coverUrl ? 'Replace photo' : 'Upload photo'}
                  </Button>
                  {job.coverUrl && (
                    <Button type="button" variant="ghost" size="sm" className="text-destructive" onClick={() => void remove()} disabled={cover.remove.isPending}>
                      <Trash2Icon /> Remove
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Accent colour */}
        <div className="grid gap-2">
          <Label>Accent colour</Label>
          <div className="flex flex-wrap items-center gap-2">
            {JOB_ACCENT_PRESETS.map((p) => {
              const active = accent.toUpperCase() === p.hex;
              return (
                <button
                  key={p.hex}
                  type="button"
                  disabled={!canEdit}
                  onClick={() => {
                    setAccent(p.hex);
                    setCustom('');
                  }}
                  aria-label={p.name}
                  aria-pressed={active}
                  title={p.name}
                  className={cn('grid size-8 place-items-center border-2 transition-transform outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed', active ? 'border-foreground scale-105' : 'border-transparent hover:scale-105')}
                  style={{ backgroundColor: p.hex }}
                >
                  {active && <CheckIcon className="size-4 text-white" aria-hidden />}
                </button>
              );
            })}
            <div className="ml-1 flex items-center gap-2">
              <Input
                value={custom}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  setCustom(v);
                  if (HEX_COLOR_RE.test(v)) setAccent(v.toUpperCase());
                }}
                placeholder="#RRGGBB"
                aria-label="Custom colour"
                aria-invalid={!customValid}
                disabled={!canEdit}
                className="h-8 w-28 font-mono text-xs uppercase"
                maxLength={7}
              />
              {accent && canEdit && (
                <button type="button" className="text-muted-foreground hover:text-foreground text-xs underline-offset-4 hover:underline" onClick={() => { setAccent(''); setCustom(''); }}>
                  Use default
                </button>
              )}
            </div>
          </div>
          {!customValid && <p className="text-destructive text-xs">Use a six-digit hex colour like #1D4ED8.</p>}
        </div>

        {/* Icon */}
        <div className="grid gap-2">
          <Label>Icon</Label>
          <div className="grid grid-cols-6 gap-1.5 sm:grid-cols-9 lg:grid-cols-12">
            {JOB_ICONS.map((i) => {
              const active = icon === i.key;
              return (
                <button
                  key={i.key}
                  type="button"
                  disabled={!canEdit}
                  onClick={() => setIcon(active ? '' : i.key)}
                  aria-label={i.label}
                  aria-pressed={active}
                  title={i.label}
                  className={cn('grid aspect-square place-items-center border transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed', active ? 'border-foreground bg-foreground text-background' : 'hover:bg-muted text-muted-foreground hover:text-foreground')}
                >
                  <i.icon className="size-4" strokeWidth={1.75} aria-hidden />
                </button>
              );
            })}
          </div>
        </div>

        {/* Preview + save */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-t pt-4">
          <div className="flex items-center gap-3">
            <JobMark job={preview} size="lg" />
            <div className="min-w-0">
              <div className="font-medium">{job.name}</div>
              <div className="text-muted-foreground text-xs">How the job appears in lists{icon ? ` · ${JOB_ICONS.find((i) => i.key === icon)?.label ?? ''}` : ''}</div>
            </div>
          </div>
          {canEdit && (
            <Button type="button" onClick={() => void save()} disabled={!dirty || !customValid || update.isPending}>
              {update.isPending ? 'Saving…' : 'Save appearance'}
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}

function QuietHoursSection() {
  const { job, permissions } = useJobContext();
  const me = useMe();
  const update = useUpdateJob(job.id);
  const org = me.tenant.quietHours;
  const override = job.settings.quietHours ?? {};
  const [enabled, setEnabled] = useState(override.enabled ?? org.enabled);
  const [start, setStart] = useState(override.start ?? org.start);
  const [end, setEnd] = useState(override.end ?? org.end);
  const isOverridden = Object.keys(override).length > 0;
  const dirty = enabled !== (override.enabled ?? org.enabled) || start !== (override.start ?? org.start) || end !== (override.end ?? org.end);

  const save = async () => {
    try {
      await update.mutateAsync({ settings: { quietHours: { enabled, start, end } } });
      toast.success('Quiet hours saved for this job');
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <section className="bg-card border p-6">
      <SectionTitle className="mb-2">Quiet hours</SectionTitle>
      <p className="text-muted-foreground mb-4 text-sm">
        Organization default: {org.enabled ? `${org.start}–${org.end}` : 'off'}. {isOverridden ? 'This job overrides it.' : 'This job follows it; change below to override.'}
      </p>
      <div className="grid gap-4">
        <label className="flex items-center gap-3 text-sm">
          <Switch checked={enabled} onCheckedChange={setEnabled} disabled={!permissions.editJob} /> {enabled ? 'Enforced on this job' : 'Not enforced on this job'}
        </label>
        <div className="grid grid-cols-2 gap-3">
          <Field label="From" htmlFor="jq-start">
            <Input id="jq-start" type="time" value={start} onChange={(e) => setStart(e.target.value)} disabled={!permissions.editJob} />
          </Field>
          <Field label="Until" htmlFor="jq-end">
            <Input id="jq-end" type="time" value={end} onChange={(e) => setEnd(e.target.value)} disabled={!permissions.editJob} />
          </Field>
        </div>
        {permissions.editJob && (
          <div className="flex justify-end">
            <Button type="button" onClick={() => void save()} disabled={!dirty || update.isPending}>
              {update.isPending ? 'Saving…' : 'Save quiet hours'}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}

function PhoneNumberSection() {
  const { job, permissions } = useJobContext();
  const update = useUpdateJob(job.id);
  const numbers = useAvailableNumbers(permissions.assignPhoneNumber);
  const [value, setValue] = useState<string>(job.phoneNumber?.id ?? NONE);
  const dirty = value !== (job.phoneNumber?.id ?? NONE);

  const save = async () => {
    try {
      await update.mutateAsync({ phoneNumberId: value === NONE ? null : value });
      toast.success(value === NONE ? 'Phone number removed' : 'Phone number assigned');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <section className="bg-card border p-6">
      <SectionTitle className="mb-5">Phone number</SectionTitle>
      <div className="grid gap-4">
        <div>
          <Label className="text-muted-foreground text-xs uppercase tracking-wide">Current</Label>
          <p className="mt-1 text-lg font-medium tabular-nums">{job.phoneNumber?.display ?? <span className="text-muted-foreground text-base font-normal">Not assigned</span>}</p>
          {job.phoneNumber && (
            <p className="text-muted-foreground text-xs">
              {job.phoneNumber.e164} · {job.phoneNumber.provider === 'mock' ? 'Simulated number (development)' : 'Carrier number'}
            </p>
          )}
        </div>
        {permissions.assignPhoneNumber ? (
          <>
            <div className="grid gap-2">
              <Label htmlFor="phone">Change number</Label>
              <Select value={value} onValueChange={setValue}>
                <SelectTrigger id="phone" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>No number</SelectItem>
                  {job.phoneNumber && <SelectItem value={job.phoneNumber.id}>{job.phoneNumber.display} (current)</SelectItem>}
                  {(numbers.data?.items ?? []).map((n) => (
                    <SelectItem key={n.id} value={n.id}>
                      {n.display} {n.friendlyName ? `· ${n.friendlyName}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-muted-foreground text-xs">Only unassigned numbers in your organization are listed. New numbers are provisioned when creating a job or from the management console.</p>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => void save()} disabled={!dirty || update.isPending}>
                {update.isPending ? 'Saving…' : 'Update number'}
              </Button>
            </div>
          </>
        ) : (
          <p className="text-muted-foreground text-xs">Only organization admins can change a job&apos;s phone number.</p>
        )}
      </div>
    </section>
  );
}

function ArchiveSection() {
  const { job } = useJobContext();
  const update = useUpdateJob(job.id);
  const [open, setOpen] = useState(false);
  const archived = job.status === 'archived';

  const toggle = async () => {
    try {
      await update.mutateAsync({ status: archived ? 'active' : 'archived' });
      toast.success(archived ? 'Job restored' : 'Job archived');
      setOpen(false);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <section className="bg-card border p-6">
      <SectionTitle className="mb-2">
        <span className={cn(!archived && 'text-destructive')}>{archived ? 'Restore job' : 'Archive job'}</span>
      </SectionTitle>
      <p className="text-muted-foreground mb-4 text-sm">
        {archived ? 'Restoring makes the job visible to its team again and re-enables sending.' : 'Archiving hides the job from its team and stops sending. Contacts, history and the phone number assignment are kept.'}
      </p>
      <Button variant="outline" className={cn(!archived && 'border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive')} onClick={() => setOpen(true)}>
        {archived ? 'Restore job' : 'Archive job'}
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={archived ? `Restore "${job.name}"?` : `Archive "${job.name}"?`}
        description={archived ? 'The team will regain access immediately.' : 'Team members will lose access until it is restored. Nothing is deleted.'}
        confirmLabel={archived ? 'Restore' : 'Archive'}
        destructive={!archived}
        loading={update.isPending}
        onConfirm={toggle}
      />
    </section>
  );
}
